import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query'
import { createQueryKeys } from '@lukemorales/query-key-factory'
import {
  queryClient,
  type MutationHook,
  type QueryHook,
} from '@/lib/react-query'
import type {
  GetVariablesParams,
  GetVariableParams,
  CreateVariableParams,
  UpdateVariableParams,
  DeleteVariableParams,
} from '../model/params'
import {
  createVariable,
  getVariables,
  getVariable,
  updateVariable,
  deleteVariable,
} from './requests'
import type { TextVariableDto } from '../model/dtos'
import type { TextVariables } from '~shared/types'
import { rundownPageStore } from '@/features/rundown/rundown-page.store'

export const variablesQueryKeys = createQueryKeys('VARIABLES', {
  list: ({ rundownId, ...params }: GetVariablesParams) => [
    `rundownId:${rundownId}`,
    params,
  ],
  byVariableKey: ({ rundownId, variableKey, ...params }: GetVariableParams) => [
    `rundownId:${rundownId}`,
    variableKey,
    params,
  ],
})

export const useVariables = ({
  params,
  options,
}: QueryHook<TextVariables, GetVariablesParams>) => {
  return useQuery({
    queryKey: variablesQueryKeys.list(params).queryKey,
    queryFn: async () => {
      const { data } = await getVariables(params)

      return data
    },
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export const useVariable = ({
  params,
  options,
}: QueryHook<TextVariableDto, GetVariableParams>) => {
  return useQuery({
    queryKey: variablesQueryKeys.byVariableKey(params).queryKey,
    queryFn: async () => {
      const { data } = await getVariable(params)
      return data
    },
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    ...options,
  })
}

export const useCreateVariable = ({
  options,
}: MutationHook<TextVariableDto, CreateVariableParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await createVariable(params)

      return data
    },
    ...options,
  })
}

export const useUpdateVariable = ({
  options,
}: MutationHook<TextVariableDto, UpdateVariableParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await updateVariable(params)
      return data
    },
    ...options,
  })
}

export const useDeleteVariable = ({
  options,
}: MutationHook<unknown, DeleteVariableParams> = {}) => {
  return useMutation({
    mutationFn: async (params) => {
      const { data } = await deleteVariable(params)

      return data
    },
    ...options,
  })
}

/**
 * Get a variable from variables cache in tanstack query.
 * Works outside React / no `useVariable` needed.
 *
 * Temp. helper until migration.
 */
export function getVariableFromCache(
  variableKey: GetVariableParams['variableKey'],
) {
  const params = {
    rundownId: rundownPageStore.rundown.id,
    variableKey: variableKey,
  }

  return queryClient.getQueryData<TextVariableDto>(
    variablesQueryKeys.byVariableKey(params).queryKey,
  )
}
